/**
 * On-screen debug overlay for kiosk devices (e.g. Bravia autostart) where ADB/remote
 * DevTools aren't available. Enable by adding `?debug=1` to the page URL (e.g. set it
 * temporarily as the Pro Mode Entry Source URL) to mirror console output and errors
 * directly onto the screen.
 */

const DEBUG_PARAM = 'debug'
const OVERLAY_ID = 'infoboard-debug-overlay'
const MAX_LINES = 40

const CONSOLE_LEVELS = ['log', 'warn', 'error'] as const
type ConsoleLevel = typeof CONSOLE_LEVELS[number]

function isDebugEnabled(): boolean {
	return new URLSearchParams(window.location.search).has(DEBUG_PARAM)
}

function createOverlay(): HTMLDivElement {
	const overlay = document.createElement('div')
	overlay.id = OVERLAY_ID
	Object.assign(overlay.style, {
		position: 'fixed',
		bottom: '0',
		left: '0',
		right: '0',
		maxHeight: '45vh',
		overflowY: 'auto',
		background: 'rgba(0, 0, 0, 0.85)',
		color: '#0f0',
		fontFamily: 'monospace',
		fontSize: '13px',
		lineHeight: '1.4',
		padding: '8px',
		zIndex: '2147483647',
		whiteSpace: 'pre-wrap',
		pointerEvents: 'none',
	})
	document.body.appendChild(overlay)
	return overlay
}

function formatArg(arg: unknown): string {
	if (typeof arg === 'string') return arg
	try {
		return JSON.stringify(arg)
	} catch {
		return String(arg)
	}
}

function appendLine(overlay: HTMLDivElement, level: ConsoleLevel | 'info', args: unknown[]): void {
	const line = document.createElement('div')
	const timestamp = new Date().toISOString().slice(11, 23)
	line.textContent = `[${timestamp}] ${level.toUpperCase()}: ${args.map(formatArg).join(' ')}`

	if (level === 'error') line.style.color = '#f66'
	else if (level === 'warn') line.style.color = '#fd0'

	overlay.appendChild(line)

	while (overlay.children.length > MAX_LINES) {
		overlay.firstElementChild?.remove()
	}

	overlay.scrollTop = overlay.scrollHeight
}

/** Mirrors console output and uncaught errors onto a fixed overlay when `?debug=1` is present. */
export function startDebugOverlay(): void {
	if (!isDebugEnabled() || document.getElementById(OVERLAY_ID)) {
		return
	}

	const overlay = createOverlay()

	CONSOLE_LEVELS.forEach((level) => {
		const original = console[level].bind(console)
		console[level] = (...args: unknown[]) => {
			original(...args)
			appendLine(overlay, level, args)
		}
	})

	window.addEventListener('error', (event) => {
		appendLine(overlay, 'error', [`Uncaught: ${event.message} (${event.filename}:${event.lineno})`])
	})

	window.addEventListener('unhandledrejection', (event) => {
		appendLine(overlay, 'error', [`Unhandled rejection: ${String(event.reason)}`])
	})

	appendLine(overlay, 'info', [`navigator.onLine = ${navigator.onLine}`])
}
